import React, { useCallback, useRef, useState } from 'react';
import {
  Music2, Film, Wand2, Sparkles, Upload, Loader2,
  Download, Play, Image as ImageIcon, Zap, AlertTriangle,
} from 'lucide-react';

/**
 * NextXus Studio Labs (/labs) — Aria's creative suite.
 * Architect directive · Feb 2026.
 *
 * Three DeepAI-powered creative tools, all routed through backend proxies so
 * the DEEPAI_API_KEY stays server-side:
 *   /api/labs/music          — AI Music Generator
 *   /api/labs/video          — AI Video Generator
 *   /api/labs/photo-editor   — AI Photo Editor
 */

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────
const pickMediaUrl = (result) => {
  // DeepAI responses vary: {output_url}, or {output: {audio: ...}} etc.
  if (!result || typeof result !== 'object') return null;
  return (
    result.output_url ||
    result.output?.audio ||
    result.output?.video ||
    result.output?.image ||
    (typeof result.output === 'string' ? result.output : null) ||
    null
  );
};

// ── Tool card shell ──────────────────────────────────────────────────────
const ToolCard = ({ icon: Icon, title, subtitle, accent, testId, children }) => (
  <article
    data-testid={testId}
    style={{
      display: 'flex', flexDirection: 'column', gap: '0.9rem',
      padding: '1.5rem 1.6rem',
      borderRadius: '18px',
      border: `1px solid ${accent}33`,
      background: `linear-gradient(180deg, ${accent}12, rgba(0,0,0,0.55))`,
      boxShadow: `0 12px 40px ${accent}18`,
      backdropFilter: 'blur(10px)',
    }}
  >
    <header style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44, borderRadius: 12,
          background: `linear-gradient(135deg, ${accent}, ${accent}80)`,
          color: '#0b0817',
        }}
      >
        <Icon size={22} strokeWidth={2.2} />
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{
          margin: 0, fontSize: '1.05rem', fontWeight: 900, color: '#fff',
          letterSpacing: '0.02em',
        }}>{title}</h3>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#a892b1' }}>{subtitle}</p>
      </div>
    </header>
    {children}
  </article>
);

// ── Music tool ───────────────────────────────────────────────────────────
const MusicTool = () => {
  const [prompt, setPrompt] = useState('warm cinematic ambient with strings and soft piano');
  const [state, setState] = useState({ loading: false, error: null, result: null });

  const run = useCallback(async () => {
    if (!prompt.trim()) return;
    setState({ loading: true, error: null, result: null });
    try {
      const fd = new FormData();
      fd.append('text', prompt.trim());
      const r = await fetch(`${API}/api/labs/music`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setState({ loading: false, error: null, result: data });
    } catch (e) {
      setState({ loading: false, error: e.message, result: null });
    }
  }, [prompt]);

  const url = pickMediaUrl(state.result);

  return (
    <ToolCard
      icon={Music2}
      title="AI Music Generator"
      subtitle="Describe a mood or genre — receive an original score."
      accent="#ff8fcf"
      testId="labs-tool-music"
    >
      <textarea
        data-testid="labs-music-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        placeholder="e.g. 'uplifting synthwave with driving bass'"
        style={{
          width: '100%', padding: '0.65rem 0.8rem',
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
          fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.5, resize: 'vertical',
        }}
      />
      <button
        type="button"
        data-testid="labs-music-submit"
        disabled={state.loading || !prompt.trim()}
        onClick={run}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
          padding: '0.65rem 1.1rem', borderRadius: 10,
          background: state.loading ? 'rgba(255,143,207,0.25)' : 'linear-gradient(135deg, #ff8fcf, #ff5fa3)',
          border: 'none', color: state.loading ? '#a892b1' : '#0b0817',
          fontSize: '0.88rem', fontWeight: 800, letterSpacing: '0.02em',
          cursor: state.loading ? 'wait' : 'pointer',
        }}
      >
        {state.loading ? <Loader2 size={14} className="labs-spin" /> : <Sparkles size={14} />}
        {state.loading ? 'Composing…' : 'Generate music'}
      </button>
      {state.error && (
        <div data-testid="labs-music-error" style={errStyle}>{state.error}</div>
      )}
      {url && (
        <div data-testid="labs-music-result" style={{ display: 'grid', gap: '0.4rem' }}>
          <audio controls src={url} style={{ width: '100%' }} />
          <a href={url} target="_blank" rel="noopener noreferrer" style={dlStyle}>
            <Download size={12} /> Download track
          </a>
        </div>
      )}
    </ToolCard>
  );
};

// ── Video tool ───────────────────────────────────────────────────────────
const VideoTool = () => {
  const [prompt, setPrompt] = useState('a slow drone shot flying over a neon city at dusk');
  const [state, setState] = useState({ loading: false, error: null, result: null });

  const run = useCallback(async () => {
    if (!prompt.trim()) return;
    setState({ loading: true, error: null, result: null });
    try {
      const fd = new FormData();
      fd.append('text', prompt.trim());
      const r = await fetch(`${API}/api/labs/video`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setState({ loading: false, error: null, result: data });
    } catch (e) {
      setState({ loading: false, error: e.message, result: null });
    }
  }, [prompt]);

  const url = pickMediaUrl(state.result);

  return (
    <ToolCard
      icon={Film}
      title="AI Video Generator"
      subtitle="Sketch a scene in words — receive a short video."
      accent="#00e5ff"
      testId="labs-tool-video"
    >
      <textarea
        data-testid="labs-video-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        placeholder="e.g. 'a lighthouse in a storm at dawn'"
        style={{
          width: '100%', padding: '0.65rem 0.8rem',
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
          fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.5, resize: 'vertical',
        }}
      />
      <button
        type="button"
        data-testid="labs-video-submit"
        disabled={state.loading || !prompt.trim()}
        onClick={run}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
          padding: '0.65rem 1.1rem', borderRadius: 10,
          background: state.loading ? 'rgba(0,229,255,0.2)' : 'linear-gradient(135deg, #00e5ff, #0099cc)',
          border: 'none', color: state.loading ? '#a892b1' : '#0b0817',
          fontSize: '0.88rem', fontWeight: 800, letterSpacing: '0.02em',
          cursor: state.loading ? 'wait' : 'pointer',
        }}
      >
        {state.loading ? <Loader2 size={14} className="labs-spin" /> : <Play size={14} />}
        {state.loading ? 'Rendering…' : 'Generate video'}
      </button>
      {state.error && (
        <div data-testid="labs-video-error" style={errStyle}>{state.error}</div>
      )}
      {url && (
        <div data-testid="labs-video-result" style={{ display: 'grid', gap: '0.4rem' }}>
          <video controls src={url} style={{ width: '100%', borderRadius: 8, background: '#000' }} />
          <a href={url} target="_blank" rel="noopener noreferrer" style={dlStyle}>
            <Download size={12} /> Download clip
          </a>
        </div>
      )}
    </ToolCard>
  );
};

// ── Photo editor tool ────────────────────────────────────────────────────
const PhotoEditorTool = () => {
  const [prompt, setPrompt] = useState('make the sky a golden sunset and add warm lighting');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [state, setState] = useState({ loading: false, error: null, result: null });
  const fileInputRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setState({ loading: false, error: null, result: null });
  };

  const run = useCallback(async () => {
    if (!prompt.trim() || !file) return;
    setState({ loading: true, error: null, result: null });
    try {
      const fd = new FormData();
      fd.append('text', prompt.trim());
      fd.append('image', file);
      const r = await fetch(`${API}/api/labs/photo-editor`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setState({ loading: false, error: null, result: data });
    } catch (e) {
      setState({ loading: false, error: e.message, result: null });
    }
  }, [prompt, file]);

  const url = pickMediaUrl(state.result);

  return (
    <ToolCard
      icon={Wand2}
      title="AI Photo Editor"
      subtitle="Upload a photo, describe the edit — see the vision realised."
      accent="#ffd700"
      testId="labs-tool-photo"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        data-testid="labs-photo-file"
        onChange={onFile}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        data-testid="labs-photo-file-btn"
        onClick={() => fileInputRef.current?.click()}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
          padding: '0.6rem 1rem', borderRadius: 10,
          background: 'rgba(255,215,0,0.08)', border: '1px dashed rgba(255,215,0,0.5)',
          color: '#ffd700', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
        }}
      >
        <Upload size={14} /> {file ? file.name : 'Upload image…'}
      </button>
      {preview && (
        <img
          src={preview}
          alt="preview"
          style={{ width: '100%', borderRadius: 10, maxHeight: 220, objectFit: 'cover' }}
        />
      )}
      <textarea
        data-testid="labs-photo-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        placeholder="e.g. 'turn day into night, add glowing city lights'"
        style={{
          width: '100%', padding: '0.65rem 0.8rem',
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
          fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.5, resize: 'vertical',
        }}
      />
      <button
        type="button"
        data-testid="labs-photo-submit"
        disabled={state.loading || !prompt.trim() || !file}
        onClick={run}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
          padding: '0.65rem 1.1rem', borderRadius: 10,
          background: state.loading ? 'rgba(255,215,0,0.25)' : 'linear-gradient(135deg, #ffd700, #ff8c00)',
          border: 'none', color: state.loading ? '#a892b1' : '#0b0817',
          fontSize: '0.88rem', fontWeight: 800, letterSpacing: '0.02em',
          cursor: state.loading ? 'wait' : (file ? 'pointer' : 'not-allowed'),
          opacity: (!file && !state.loading) ? 0.6 : 1,
        }}
      >
        {state.loading ? <Loader2 size={14} className="labs-spin" /> : <ImageIcon size={14} />}
        {state.loading ? 'Editing…' : 'Edit photo'}
      </button>
      {state.error && (
        <div data-testid="labs-photo-error" style={errStyle}>{state.error}</div>
      )}
      {url && (
        <div data-testid="labs-photo-result" style={{ display: 'grid', gap: '0.4rem' }}>
          <img src={url} alt="edited" style={{ width: '100%', borderRadius: 10 }} />
          <a href={url} target="_blank" rel="noopener noreferrer" style={dlStyle}>
            <Download size={12} /> Download image
          </a>
        </div>
      )}
    </ToolCard>
  );
};

// ── Image generation tool (DeepAI text2img) ─────────────────────────────
const ImageTool = () => {
  const [prompt, setPrompt] = useState('a luminous cathedral of consciousness floating among stars');
  const [state, setState] = useState({ loading: false, error: null, result: null });

  const run = useCallback(async () => {
    if (!prompt.trim()) return;
    setState({ loading: true, error: null, result: null });
    try {
      const fd = new FormData();
      fd.append('text', prompt.trim());
      const r = await fetch(`${API}/api/labs/image`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setState({ loading: false, error: null, result: data });
    } catch (e) {
      setState({ loading: false, error: e.message, result: null });
    }
  }, [prompt]);

  const url = pickMediaUrl(state.result);

  return (
    <ToolCard
      icon={ImageIcon}
      title="AI Image Generation"
      subtitle="Describe any vision — receive original artwork."
      accent="#9d4edd"
      testId="labs-tool-image"
    >
      <textarea
        data-testid="labs-image-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        placeholder="e.g. 'a glowing red rose in studio lighting'"
        style={{
          width: '100%', padding: '0.65rem 0.8rem',
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
          fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.5, resize: 'vertical',
        }}
      />
      <button
        type="button"
        data-testid="labs-image-submit"
        disabled={state.loading || !prompt.trim()}
        onClick={run}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
          padding: '0.65rem 1.1rem', borderRadius: 10,
          background: state.loading ? 'rgba(157,78,221,0.25)' : 'linear-gradient(135deg, #9d4edd, #6a2fbf)',
          border: 'none', color: state.loading ? '#a892b1' : '#fff',
          fontSize: '0.88rem', fontWeight: 800, letterSpacing: '0.02em',
          cursor: state.loading ? 'wait' : 'pointer',
        }}
      >
        {state.loading ? <Loader2 size={14} className="labs-spin" /> : <Sparkles size={14} />}
        {state.loading ? 'Painting…' : 'Generate image'}
      </button>
      {state.error && (
        <div data-testid="labs-image-error" style={errStyle}>{state.error}</div>
      )}
      {url && (
        <div data-testid="labs-image-result" style={{ display: 'grid', gap: '0.4rem' }}>
          <img src={url} alt="generated" style={{ width: '100%', borderRadius: 10 }} />
          <a href={url} target="_blank" rel="noopener noreferrer" style={dlStyle}>
            <Download size={12} /> Download image
          </a>
        </div>
      )}
    </ToolCard>
  );
};

const errStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
  padding: '0.55rem 0.7rem', borderRadius: 8,
  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)',
  color: '#fca5a5', fontSize: '0.78rem', lineHeight: 1.45,
};

const dlStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
  fontSize: '0.75rem', color: '#a892b1', textDecoration: 'none',
  padding: '0.25rem 0.5rem',
};

const StudioLabs = () => (
  <main
    data-testid="studio-labs-root"
    style={{
      minHeight: '100vh',
      background:
        'radial-gradient(ellipse at 15% -10%, rgba(255, 143, 207, 0.16) 0%, transparent 55%),' +
        'radial-gradient(ellipse at 85% 20%, rgba(0, 229, 255, 0.14) 0%, transparent 55%),' +
        'radial-gradient(ellipse at 50% 110%, rgba(255, 215, 0, 0.10) 0%, transparent 55%),' +
        '#0a0714',
      color: '#fff',
      padding: '3rem 1rem 6rem',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: '1.75rem' }}>
      {/* Header */}
      <header style={{ textAlign: 'center' }}>
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
            padding: '0.4rem 1rem',
            background: 'rgba(255,143,207,0.1)',
            border: '1px solid rgba(255,143,207,0.45)',
            borderRadius: '999px',
            fontSize: '0.72rem', color: '#ff8fcf',
            letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
          }}
        >
          <Sparkles size={13} /> Studio Pillar · AI Labs
        </span>
        <h1
          data-testid="studio-labs-headline"
          style={{
            margin: '1rem 0 0.6rem', fontSize: 'clamp(2.4rem, 5.5vw, 3.8rem)', fontWeight: 900,
            lineHeight: 1.02,
            background: 'linear-gradient(135deg, #ff8fcf 0%, #ffd700 45%, #00e5ff 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}
        >
          NextXus Studio Labs
        </h1>
        <p
          data-testid="studio-labs-subtitle"
          style={{
            color: '#cbb8d4', fontSize: '1.02rem', maxWidth: 640,
            margin: '0 auto', lineHeight: 1.6, fontStyle: 'italic',
          }}
        >
          Powered by NextXus — Visualize Your Thought
        </p>
        <div
          data-testid="studio-labs-badge"
          style={{
            marginTop: '1.1rem',
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.42rem 0.9rem', borderRadius: '999px',
            background: 'linear-gradient(135deg, rgba(74,222,128,0.15), rgba(255,215,0,0.15))',
            border: '1px solid rgba(74,222,128,0.4)',
            color: '#4ade80', fontSize: '0.72rem',
            letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 800,
          }}
        >
          <Zap size={13} /> Free for all NextXus Citizens
        </div>
      </header>

      {/* Tool grid */}
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.35rem',
        }}
      >
        <MusicTool />
        <VideoTool />
        <ImageTool />
        <PhotoEditorTool />
      </section>

      {/* Legal note */}
      <footer
        style={{
          textAlign: 'center', color: '#7a6e80', fontSize: '0.78rem',
          fontStyle: 'italic', marginTop: '1rem', lineHeight: 1.6, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto',
        }}
      >
        <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4, color: '#ffd700' }} />
        Generations are streamed live from fal.ai and DeepAI — output quality varies with prompt. Uploaded photos are proxied through our backend and not retained by the Aria node beyond this request.
      </footer>
    </div>
    <style>{`
      @keyframes labs-spin { to { transform: rotate(360deg); } }
      .labs-spin { animation: labs-spin 1s linear infinite; }
    `}</style>
  </main>
);

export default StudioLabs;
